import { ReactElement, useEffect, useMemo } from 'react';
import { Grid, SelectChangeEvent, useEventCallback } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { useFormikContext } from 'formik';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { useListener } from 'shell/hooks/use-bus';
import { EventFormPractice } from 'practice/constants/research-state-enum';
import { ResearchAct } from 'civil-status/view-models/research-category-form';
import ListRegister from '../../../../shared/list-register';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';

const defaultInitialValues = {
  idCat: undefined,
  dateEnd: undefined,
  dateStart: undefined,
  idAct: undefined,
} as ResearchAct;

interface FormFilterProps {
  idCat?: string;
  handleSubmitResearchAct: (values: ResearchAct) => void;
  disabledRegister?: boolean;
  disabledMunicipality?: boolean;
  isWithoutInitialContext?: boolean;
}

const FormFilter = ({
  idCat,
  handleSubmitResearchAct,
  disabledRegister,
  disabledMunicipality,
  isWithoutInitialContext,
}: FormFilterProps): ReactElement => {
  const formContext = useFormikContext<ResearchAct>();

  const filterTable = useFilterTable<ResearchAct>();

  useListener<ResearchAct>(EventFormPractice.UpdateForm, (form) => {
    formContext.setValues({ ...formContext.values, ...form });
  });

  useEffect(() => {
    filterTable.setValues(formContext.values);
  }, [formContext.values]);

  useEffect(() => {
    if (filterTable.values && !isWithoutInitialContext) {
      formContext.setValues(filterTable.values || defaultInitialValues);
    }
  }, []);

  const onChangeRegister = useEventCallback((event: SelectChangeEvent<string>) => {
    formContext.setFieldValue('idCat', event.target.value);
  });

  const handleMunicipalityListChange = useEventCallback(
    (municipality: MunicipalityAnprVM | null) => {
      formContext.setFieldValue('municipality', municipality);
    }
  );

  const withErrors: boolean = useMemo(() => {
    return Boolean(Object.keys(formContext.errors || {})?.length);
  }, [formContext.errors]);

  const onSearch = useEventCallback(() => {
    formContext.setTouched({
      idCat: true,
      dateEnd: true,
      dateStart: true,
      idAct: true,
      municipality: true,
      stateAct: true,
      idEvent: true,
    });

    if (withErrors) {
      return;
    }

    handleSubmitResearchAct({
      ...(formContext.values || {}),
      idCat: formContext.values.disabledRegister ? idCat || '' : formContext.values.idCat || '',
    });
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <ListRegister
          value={idCat || formContext.values.idCat || ''}
          error={Boolean(formContext.errors.idCat) && formContext.touched.idCat}
          onChange={onChangeRegister}
          disabled={Boolean(idCat) || disabledRegister}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <DatePicker
          label="Data inizio"
          aria-label="Data inizio"
          value={formContext.values.dateStart || null}
          onChange={(value) => {
            if (formContext.values.dateEnd) {
              formContext.setFieldValue('dateStart', value);
              return;
            }

            formContext.setValues({
              ...formContext.values,
              dateStart: value,
              dateEnd: value,
            });
          }}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="dateStart"
              fullWidth
              error={formContext.touched?.dateStart && Boolean(formContext.errors?.dateStart)}
              helperText={formContext.errors?.dateStart}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <DatePicker
          label="Data fine"
          aria-label="Data fine"
          value={formContext.values.dateEnd || null}
          onChange={(value) => formContext.setFieldValue('dateEnd', value)}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="dateEnd"
              fullWidth
              error={formContext.touched?.dateEnd && Boolean(formContext.errors?.dateEnd)}
              helperText={formContext.errors?.dateEnd}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <MunicipalityList
          value={formContext.values?.municipality || null}
          onChange={handleMunicipalityListChange}
          label="Comune"
          fromDate={formContext.values.dateStart || null}
          error={formContext.touched.municipality && Boolean(formContext.errors.municipality)}
          disabled={disabledMunicipality}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          aria-label="ID Ansc"
          label="ID Ansc"
          id="idAct"
          name="idAct"
          type="text"
          variant="outlined"
          value={formContext.values.idAct || ''}
          fullWidth
          onChange={formContext.handleChange}
          error={formContext.touched.idAct && Boolean(formContext.errors.idAct)}
          helperText={formContext.errors.idAct}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          aria-label="ID Nazionale"
          label="ID Nazionale"
          id="idEvent"
          name="idEvent"
          type="number"
          variant="outlined"
          value={formContext.values.idEvent || ''}
          fullWidth
          onChange={formContext.handleChange}
          error={formContext.touched.idEvent && Boolean(formContext.errors.idEvent)}
          helperText={formContext.errors.idEvent}
        />
      </Grid>
      <Grid item container spacing={2} xs={12} justifyContent="space-between" display="flex">
        <Grid item container spacing={2} xs={12} justifyContent="flex-end" display="flex">
          <Grid item xs={12} md={2}>
            <PmiButton
              variant="contained"
              color="secondary"
              fullWidth
              onClick={() => formContext.resetForm()}
            >
              Pulisci
            </PmiButton>
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiButton variant="contained" color="secondary" fullWidth onClick={onSearch}>
              Cerca
            </PmiButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

FormFilter.displayName = 'Practice Research - Filter Practice - Form Filter component';

export default FormFilter;
